import { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { Alert, Button } from 'react-daisyui';
import useSaveFamilyOnboarding from '@/api/useSaveFamilyOnboarding.ts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from '@/utils/analytics.ts';
import { isValidUkPostcode, normaliseUkPostcode } from '@/utils/postcodeDistance.ts';

const steps = ['care', 'details', 'summary'] as const;

const careStepSchema = z.object({
  who_needs_care: z.string().min(1, { message: 'Required' }),
  care_type: z.string().min(1, { message: 'Required' }),
  frequency: z.string().min(1, { message: 'Required' }),
});

type CareStepSchema = z.infer<typeof careStepSchema>;

const detailsStepSchema = z.object({
  urgency: z.string().min(1, { message: 'Required' }),
  start_type: z.enum(['ongoing', 'one-off'], { required_error: 'Required' }),
  start_date: z.string().optional(),
  location: z
    .string()
    .min(1, { message: 'Required' })
    .transform((v) => normaliseUkPostcode(v))
    .refine((v) => isValidUkPostcode(v), { message: 'Enter a valid UK postcode' }),
  hourly_budget: z.coerce.number().optional(),
});

type DetailsStepSchema = z.infer<typeof detailsStepSchema>;

type OnboardingData = CareStepSchema & DetailsStepSchema & { email?: string };

const whoNeedsCareOptions: { value: string; label: string }[] = [
  { value: 'self', label: 'Self' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other' },
];

const careTypeOptions: { value: string; label: string }[] = [
  { value: 'personal-care', label: 'Personal care' },
  { value: 'household-help', label: 'Household help' },
  { value: 'companionship', label: 'Companionship' },
  { value: 'other', label: 'Other' },
];

const urgencyOptions: { value: string; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'soon', label: 'Soon' },
  { value: 'flexible', label: 'Flexible' },
];

const OnboardingPage: FC = () => {
  const { session, currentUser, logout } = useAuthContext();
  const navigate = useNavigate();

  // Check if user has family_member role
  const isFamilyMember = currentUser?.role === 'family_member';

  // Redirect business providers to their first navigation tab (intro calls)
  useEffect(() => {
    if (session && !isFamilyMember) {
      navigate(Routes.INTRO_CALLS, { replace: true });
    }
  }, [session, isFamilyMember, navigate]);

  if (session && !isFamilyMember) {
    return null;
  }

  return (
    <div className="w-full min-h-[80vh] flex flex-col items-center justify-center py-8 md:py-12 gap-4">
      <Stepper.Wrapper steps={steps} initialStep="care">
        <Stepper.Step<typeof steps> step="care">
          <StepCare />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="details">
          <StepDetails />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="summary">
          <StepSummary />
        </Stepper.Step>
      </Stepper.Wrapper>
      <button
        type="button"
        onClick={logout}
        className="text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
      >
        Sign out
      </button>
    </div>
  );
};

export default OnboardingPage;

function ProgressBar() {
  const { step } = useStepperContext<typeof steps, OnboardingData>();

  const stepIndex = steps.indexOf(step as (typeof steps)[number]);

  return (
    <div className="w-full flex items-center gap-2 text-xs text-gray-500">
      {steps.map((s, index) => (
        <div key={s} className="flex-1 flex items-center gap-2">
          <div
            className={`h-[2px] flex-1 ${index <= stepIndex ? 'bg-black' : 'bg-gray-200'}`}
          />
          {index < steps.length - 1 ? null : (
            <div className={`w-2 h-2 rounded-full ${stepIndex === steps.length - 1 ? 'bg-black' : 'bg-gray-300'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function StepCare() {
  const { t } = useTranslation();
  const { goToStep, setContextData, contextData } = useStepperContext<typeof steps, OnboardingData>();
  const [searchParams] = useSearchParams();
  const { session } = useAuthContext();
  const email = searchParams.get('email') ?? contextData?.email ?? session?.user?.email ?? undefined;

  const { control, handleSubmit } = useForm<CareStepSchema>({
    resolver: zodResolver(careStepSchema),
    defaultValues: {
      who_needs_care: contextData?.who_needs_care ?? '',
      care_type: contextData?.care_type ?? '',
      frequency: contextData?.frequency ?? '',
    },
  });

  const handleNext = (data: CareStepSchema) => {
    setContextData((prev) => ({ ...prev, ...data, email: email ?? prev?.email }));
    goToStep('details');
  };

  return (
    <BrandCardSideContent>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleNext)}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">{t('onboardingPage.title')}</h1>
              <p className="text-sm text-text-secondary">{t('onboardingPage.subtitle')}</p>
            </div>
          <Controller
            control={control}
            name="who_needs_care"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <SelectInput
                value={value}
                onChange={onChange}
                options={whoNeedsCareOptions}
                placeholder={t('onboardingPage.whoNeedsCarePlaceholder')}
                error={errors.who_needs_care?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="care_type"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <SelectInput
                value={value}
                onChange={onChange}
                options={careTypeOptions}
                placeholder={t('onboardingPage.careTypePlaceholder')}
                error={errors.care_type?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="frequency"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <SelectInput
                value={value}
                onChange={onChange}
                options={[
                  { value: 'hours-day', label: t('onboardingPage.frequencyHoursPerDay') },
                  { value: 'days-week', label: t('onboardingPage.frequencyDaysPerWeek') },
                ]}
                placeholder={t('onboardingPage.frequencyPlaceholder')}
                error={errors.frequency?.message}
              />
            )}
          />
        </div>
        <LoadingButton type="submit" color="primary" loading={false}>
          {t('button.continue')}
        </LoadingButton>
        </form>
      </div>
    </BrandCardSideContent>
  );
}

function StepDetails() {
  const { t } = useTranslation();
  const { goToStep, setContextData, contextData, previousStep } = useStepperContext<typeof steps, OnboardingData>();
  const [searchParams] = useSearchParams();
  const { session, currentUser } = useAuthContext();
  const email = searchParams.get('email') ?? contextData?.email ?? session?.user?.email ?? undefined;
  const registeredPostcode = currentUser?.postcode ?? '';

  const { control, handleSubmit, watch, setValue } = useForm<DetailsStepSchema>({
    resolver: zodResolver(detailsStepSchema),
    defaultValues: {
      urgency: contextData?.urgency ?? '',
      start_type: contextData?.start_type ?? 'ongoing',
      start_date: contextData?.start_date ?? '',
      location: registeredPostcode || (contextData?.location ?? ''),
      hourly_budget: contextData?.hourly_budget ?? ('' as any),
    },
  });

  // currentUser (and its postcode) loads asynchronously after login/verification, so it
  // may not be ready yet when this form's defaultValues are captured at mount. Sync the
  // field once it arrives, so the registration postcode always ends up displayed here.
  useEffect(() => {
    if (registeredPostcode) {
      setValue('location', registeredPostcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredPostcode]);

  const handleNext = (data: DetailsStepSchema) => {
    setContextData((prev) => ({ ...prev, ...data, email: email ?? prev?.email }));
    goToStep('summary');
  };

  return (
    <BrandCardSideContent>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleNext)}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h1 className="text-xl font-semibold">{t('onboardingPage.title')}</h1>
              <p className="text-sm text-text-secondary">{t('onboardingPage.subtitle')}</p>
            </div>
          <Controller
            control={control}
            name="urgency"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <SelectInput
                value={value}
                onChange={onChange}
                options={urgencyOptions}
                placeholder={t('onboardingPage.urgencyPlaceholder')}
                error={errors.urgency?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="start_type"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <SelectInput
                value={value}
                onChange={onChange}
                options={[
                  { value: 'ongoing', label: t('onboardingPage.startTypeOngoing') },
                  { value: 'one-off', label: t('onboardingPage.startTypeOneOff') },
                ]}
                placeholder={t('onboardingPage.startTypePlaceholder')}
                error={errors.start_type?.message}
              />
            )}
          />
          {watch('start_type') === 'one-off' && (
            <Controller
              control={control}
              name="start_date"
              render={({ field: { value, onChange }, formState: { errors } }) => (
                <TextInput
                  value={value}
                  onChange={onChange}
                  placeholder={t('onboardingPage.startDatePlaceholder')}
                  error={errors.start_date?.message}
                />
              )}
            />
          )}
          <Controller
            control={control}
            name="location"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <TextInput
                value={value}
                onChange={(e) => onChange(e.target.value.toUpperCase())}
                placeholder={t('onboardingPage.locationPlaceholder')}
                label={t('onboardingPage.locationLabel')}
                required
                error={errors.location?.message}
                maxLength={10}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
              />
            )}
          />
          <Controller
            control={control}
            name="hourly_budget"
            render={({ field: { value, onChange }, formState: { errors } }) => (
              <TextInput
                type="number"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
                placeholder={t('onboardingPage.budgetPlaceholder')}
                error={errors.hourly_budget?.message}
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-2">
          <LoadingButton type="submit" color="primary" loading={false}>
            {t('button.continue')}
          </LoadingButton>
          <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
            {t('button.goBack')}
          </Button>
        </div>
        </form>
      </div>
    </BrandCardSideContent>
  );
}

function StepSummary() {
  const { t } = useTranslation();
  const { contextData, previousStep } = useStepperContext<typeof steps, OnboardingData>();
  const [searchParams] = useSearchParams();
  const { session } = useAuthContext();
  const email = searchParams.get('email') ?? contextData?.email ?? session?.user?.email ?? undefined;
  const navigate = useNavigate();
  const { mutateAsync: saveOnboarding, isPending, isError, error, isSuccess } = useSaveFamilyOnboarding();
  const queryClient = useQueryClient();

  const handleSave = () => {
    if (!email) return;

    void (async () => {
      try {
        const payload = {
          email,
          who_needs_care: contextData?.who_needs_care ?? '',
          care_type: contextData?.care_type ?? '',
          frequency: contextData?.frequency ?? '',
          urgency: contextData?.urgency ?? '',
          start_type: contextData?.start_type ?? 'ongoing',
          start_date: contextData?.start_type === 'one-off' ? contextData?.start_date : undefined,
          location: contextData?.location ?? '',
          hourly_budget: contextData?.hourly_budget,
        };

        await saveOnboarding(payload);
        trackEvent({ name: 'onboarding_completed', params: { user_role: 'family' } });
        // Zapewnij natychmiastową dostępność danych dla middleware, aby nie cofało na onboarding.
        queryClient.setQueryData(['family-onboarding', email], payload);
        // Navigate to the first tab in navigation (carers page)
        navigate(Routes.CARERS);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const summaryItems = [
    { label: t('onboardingPage.whoNeedsCareSummary'), value: contextData?.who_needs_care ?? '-' },
    { label: t('onboardingPage.careTypeSummary'), value: contextData?.care_type ?? '-' },
    { label: t('onboardingPage.frequencySummary'), value: contextData?.frequency ?? '-' },
    {
      label: t('onboardingPage.urgencySummary'),
      value:
        contextData?.start_type === 'one-off'
          ? `${contextData?.urgency ?? '-'}, ${contextData?.start_date ?? '-'}`
          : `${contextData?.urgency ?? '-'}, ${t('onboardingPage.startTypeOngoing')}`,
    },
    { label: t('onboardingPage.locationSummary'), value: contextData?.location ?? '-' },
    { label: t('onboardingPage.budgetSummary'), value: contextData?.hourly_budget ?? '-' },
  ];

  return (
    <BrandCardSideContent>
      <div className="w-[428px] max-w-full flex flex-col gap-4">
        <div className="sticky top-0 bg-white z-10 pt-5">
          <div className="mt-5">
            <ProgressBar />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold">{t('onboardingPage.summaryTitle')}</h1>
          <p className="text-sm text-text-secondary">{t('onboardingPage.subtitle')}</p>
        </div>
        {isError && <Alert status="error">{error?.message}</Alert>}
        {isSuccess && <Alert status="success">{t('onboardingPage.successInfo')}</Alert>}
        <div className="border border-dashed border-gray-300 rounded-xl p-4 flex flex-col gap-2">
          {summaryItems.map((item) => (
            <div key={item.label} className="flex items-start gap-2 text-sm">
              <span className="font-semibold">{item.label}:</span>
              <span className="text-text-secondary">{item.value || '-'}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-2">
          <LoadingButton type="button" color="primary" loading={isPending} onClick={handleSave}>
            {t('onboardingPage.saveAndContinue')}
          </LoadingButton>
          <Button type="button" onClick={previousStep} className="bg-white text-black border-none shadow-none">
            {t('button.goBack')}
          </Button>
        </div>
      </div>
    </BrandCardSideContent>
  );
}

